import React, { useRef } from 'react'
import { Control, useController, useWatch } from 'react-hook-form'
import states from 'states-us'
import { useEventListener } from 'usehooks-ts'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { AddressForm, FormRadioGroup } from '@divvy-web/skylab.form'

import { useGetCountryCodes } from '@sputnik/components/SSNPassportInput/GetCountryCodes.gql'
import { RequiredAddress } from '@sputnik/schema/creditApplication/requiredAddress'

import styles from './styles.module.scss'

interface ResidentialAddressFormProps {
  disabled?: boolean
  control: Control<RequiredAddress>
}

const ResidentialAddressForm = ({ disabled, control }: ResidentialAddressFormProps) => {
  const [getClassName] = useNameFormatter('ResidentialAddressForm')
  const { data } = useGetCountryCodes()
  const stateDropdownRef = useRef<HTMLInputElement | null>(null)

  const countryOptions =
    data?.countryCodes?.map((country) => ({
      displayName: country?.displayName,
      isoCode2: country?.isoCode2,
    })) || []

  const isForeignAddress =
    useWatch({
      control: control as unknown as Control<Pick<RequiredAddress, 'isForeignAddress'>>,
      name: 'isForeignAddress',
    }) === 'YES'
  const {
    field: { onChange },
  } = useController({
    control,
    name: 'state',
  })

  useEventListener(
    'change',
    (e) => {
      const value = (e.target as HTMLInputElement)?.value

      if (!value) {
        return
      }

      const stateSelected = states.find(({ abbreviation, name }) => value === abbreviation || value === name)

      if (stateSelected) {
        onChange(stateSelected.abbreviation)
      }
    },
    stateDropdownRef,
  )

  return (
    <div className={styles[getClassName('')]}>
      <FormRadioGroup
        control={control}
        label={
          <span className='fs-unmask'>
            <FormattedMessage
              defaultMessage='Residential address is outside the US'
              id='sputnik.ResidentialAddressForm__myMt0/'
            />
          </span>
        }
        name='isForeignAddress'
        options={[
          {
            disabled,
            label: (
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='Yes'
                  id='sputnik.ResidentialAddressForm__a5msuh'
                />
              </span>
            ),
            value: 'YES',
            id: 'foreign-address-yes',
          },
          {
            disabled,
            label: (
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='No'
                  id='sputnik.ResidentialAddressForm__oUWADl'
                />
              </span>
            ),
            value: 'NO',
            id: 'foreign-address-no',
          },
        ]}
      />
      <AddressForm
        className={isForeignAddress ? `${styles[getClassName('')]} fs-mask` : 'fs-mask'}
        control={control}
        countryCodes={isForeignAddress ? countryOptions : []}
        fields={{
          city: {
            disabled,
            label: (
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='City'
                  id='sputnik.ResidentialAddressForm__TE4fIS'
                />
              </span>
            ),
            name: 'city',
          },
          country: isForeignAddress
            ? {
                disabled,
                label: (
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Country/Region'
                      id='sputnik.ResidentialAddressForm__/V1GsM'
                    />
                  </span>
                ),
                name: 'countryCode',
                placeholder: (
                  <FormattedMessage
                    defaultMessage='Select'
                    id='sputnik.ResidentialAddressForm__kQAf2d'
                  />
                ),
              }
            : undefined,
          state: {
            disabled,
            label: (
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='{isForeignAddress, select, 
          true {State/Province}
          other {State}}'
                  id='sputnik.ResidentialAddressForm__OYgXwr'
                  values={{ isForeignAddress }}
                />
              </span>
            ),
            name: 'state',
            placeholder: (
              <FormattedMessage
                defaultMessage='Select'
                id='sputnik.ResidentialAddressForm__kQAf2d'
              />
            ),
            isDropUp: true,
            autoSelectOnBlur: false,
            ref: stateDropdownRef,
            inputProps: {
              autoComplete: 'address-level1',
              name: 'state',
              id: 'state',
              'aria-labelledby': 'state',
            },
          },
          street1: {
            disabled,
            label: (
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='Residential address'
                  id='sputnik.ResidentialAddressForm__4iXhxD'
                />
              </span>
            ),
            name: 'street',
          },
          street2: {
            disabled,
            label: (
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='Residential address line 2'
                  id='sputnik.ResidentialAddressForm__1WWwX6'
                />
              </span>
            ),
            name: 'streetAdditional',
          },
          zipCode: {
            disabled,
            label: (
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='{isForeignAddress, select, 
          true {Postal code}
          other {ZIP code}}'
                  id='sputnik.ResidentialAddressForm__iZL8VQ'
                  values={{ isForeignAddress }}
                />
              </span>
            ),
            name: 'postalCode',
          },
        }}
        googleMapsApiKey={window.__DivvyEnvironment.GOOGLE_PLACES_API_KEY}
      />
    </div>
  )
}

export default ResidentialAddressForm
